/* lp-purchase.js — v3
   Owns: size selection sync, gallery, ad-LP checkout-skip mode, discount pre-arm.

   ATC ARCHITECTURE (important — read before "fixing"):
   Palo Alto's theme.dev.js has a document-level click listener on any
   [data-add-to-cart] button. It preventDefaults, POSTs the closest <form>'s
   FormData to /cart/add, refetches the `api-cart-items` section, rebuilds the
   cart drawer contents, and opens the drawer. That is the SAME pipeline as the
   theme's native PDP add-to-cart.

   So in default (PDP/drawer) mode, this file does NOT touch the submit at all.
   The Liquid gives the button `data-add-to-cart` and the theme does the rest —
   drawer opens WITH the product. Our only jobs here:
     - keep the hidden variant id input synced to the selected size pill
       (the theme reads FormData from the form, so this is what makes the
       right variant get added)
     - pre-arm the discount cookie after the theme confirms the add
       (theme dispatches `theme:cart:added`)

   In ad-LP mode (section setting `atc_to_checkout` ON), the Liquid omits
   `data-add-to-cart`, the theme ignores the button, and we intercept submit:
   POST /cart/add.js → redirect to /discount/CODE?redirect=/checkout.
   Fetch failure → native form submit (real {% form 'product' %}) as fallback.

   TRACKING: both paths add via Shopify's cart API, which fires the native
   `product_added_to_cart` web pixel event. The Facebook & Instagram app turns
   that into Meta AddToCart (browser pixel + CAPI, deduped). Do NOT add fbq()
   calls anywhere — they would double-fire against the channel app. */
(function () {
  'use strict';

  document.querySelectorAll('[data-purchase-section]').forEach(initSection);

  /* Public helper the sticky CTA calls (lp-sticky-cta.js). Returns true if a
     size is selected; otherwise pulses the size row, scrolls to it, returns
     false. API-identical to v1 — do not rename. */
  window.lpPulseSizes = function () {
    var section = document.querySelector('[data-purchase-section]');
    if (!section) return false;
    var current = section.querySelector('[data-lp-size].is-active');
    if (current) return true;
    var sizeBtns = section.querySelectorAll('[data-lp-size]');
    sizeBtns.forEach(function (b) {
      b.classList.add('is-pulse');
      setTimeout(function () { b.classList.remove('is-pulse'); }, 1300);
    });
    if (sizeBtns[0]) {
      sizeBtns[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return false;
  };

  function initSection(section) {
    var form = section.querySelector('[data-lp-purchase-form]');
    if (form) initForm(section, form);

    var gallery = section.querySelector('[data-lp-gallery]');
    if (gallery) initGallery(gallery);
  }

  /* ── Size selection + mode-specific ATC behavior ──────────── */

  function initForm(section, form) {
    var idInput = form.querySelector('[data-lp-variant-id]');
    var atcBtn = form.querySelector('[data-lp-atc]');
    var priceSpans = section.querySelectorAll('[data-lp-price]');
    var bigPrice = section.querySelector('.lp-purchase__price');
    var installmentSpan = section.querySelector('[data-lp-installment]');
    var installmentCount = parseInt(section.dataset.installmentCount, 10) || 4;
    var discountCode = (section.dataset.discountCode || '').trim();
    var atcToCheckout = section.dataset.atcToCheckout === 'true';
    var sizeBtns = form.querySelectorAll('[data-lp-size]');
    var atcDefaultHTML = atcBtn ? atcBtn.innerHTML : '';

    /* Size pills → hidden variant id + prices. Required in BOTH modes:
       the theme's add reads FormData from this form. */
    sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('is-oos') || btn.disabled) return;

        sizeBtns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        if (idInput) idInput.value = btn.dataset.variantId;

        var priceText = btn.dataset.variantPrice;
        if (priceText) {
          priceSpans.forEach(function (s) { s.textContent = priceText; });
          if (bigPrice) bigPrice.textContent = priceText;
        }

        var cents = parseInt(btn.dataset.variantPriceCents, 10);
        if (installmentSpan && !isNaN(cents)) {
          var per = cents / installmentCount / 100;
          installmentSpan.textContent =
            '$' + (per % 1 === 0 ? per.toFixed(0) : per.toFixed(2));
        }
      });
    });

    if (!atcToCheckout) {
      /* DRAWER MODE (default / main PDP):
         The theme's [data-add-to-cart] pipeline handles the add + drawer.
         We only pre-arm the discount cookie once the theme confirms. */
      if (discountCode) {
        document.addEventListener('theme:cart:added', function armDiscount() {
          fetch('/discount/' + encodeURIComponent(discountCode), {
            credentials: 'same-origin'
          }).catch(function () {});
          document.removeEventListener('theme:cart:added', armDiscount);
        });
      }
      return;
    }

    /* CHECKOUT-SKIP MODE (ad LPs): we own the submit. */
    function resetAtc() {
      if (!atcBtn) return;
      atcBtn.classList.remove('is-busy');
      atcBtn.innerHTML = atcDefaultHTML;
    }

    // bfcache restore after Back from checkout — never leave a stuck busy state.
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted) resetAtc();
    });

    form.addEventListener('submit', function (e) {
      // Let the Shop Pay dynamic checkout button manage its own flow.
      if (e.submitter && !e.submitter.matches('[data-lp-atc]')) return;
      e.preventDefault();

      if (!idInput || !idInput.value) {
        window.lpPulseSizes();
        return;
      }

      if (atcBtn) {
        atcBtn.classList.add('is-busy');
        atcBtn.textContent = 'Adding\u2026';
      }

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (err) {
              throw new Error(err.description || err.message || 'Add failed');
            });
          }
          return res.json();
        })
        .then(function () {
          if (atcBtn) atcBtn.textContent = 'Taking you to checkout\u2026';
          if (discountCode) {
            window.location.href =
              '/discount/' + encodeURIComponent(discountCode) +
              '?redirect=' + encodeURIComponent('/checkout');
          } else {
            window.location.href = '/checkout';
          }
        })
        .catch(function () {
          // Restore, then fall back to native product-form POST — Shopify
          // adds server-side and lands on /cart. Button can't go dead.
          resetAtc();
          HTMLFormElement.prototype.submit.call(form);
        });
    });
  }

  /* ── Gallery: slides, halves-nav, dots, thumbs, swipe ─────── */

  function initGallery(gallery) {
    var slides = gallery.querySelectorAll('.lp-purchase__slide');
    var dots = gallery.querySelectorAll('[data-lp-gallery-dot]');
    var thumbs = gallery.querySelectorAll('[data-lp-gallery-thumb]');
    var prev = gallery.querySelector('[data-lp-gallery-prev]');
    var next = gallery.querySelector('[data-lp-gallery-next]');
    var current = 0;
    var count = slides.length;
    if (!count) return;

    function goTo(i) {
      current = ((i % count) + count) % count;
      slides.forEach(function (s, idx) {
        s.classList.toggle('is-active', idx === current);
        var vid = s.querySelector('video');
        if (vid) {
          if (idx === current) {
            var p = vid.play();
            if (p && typeof p.catch === 'function') p.catch(function () {});
          } else {
            vid.pause();
          }
        }
      });
      dots.forEach(function (d, idx) {
        d.classList.toggle('is-active', idx === current);
      });
      thumbs.forEach(function (t, idx) {
        t.classList.toggle('is-active', idx === current);
      });
    }

    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        goTo(parseInt(d.dataset.lpGalleryDot, 10));
      });
    });
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        goTo(parseInt(t.dataset.lpGalleryThumb, 10));
      });
    });

    var main = gallery.querySelector('.lp-purchase__gallery-main');
    if (main) {
      var startX = null;
      main.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });
      main.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) goTo(dx < 0 ? current + 1 : current - 1);
        startX = null;
      }, { passive: true });
    }
  }
})();
